import { useState, useCallback } from 'react';
import { winbackApi } from '../api/winback';
import type { CustomerWinbackCampaign, GenerateWinbackPayload } from '../api/winback';

export function useWinback() {
  const [campaigns, setCampaigns] = useState<CustomerWinbackCampaign[]>([]);
  const [activeCampaign, setActiveCampaign] = useState<CustomerWinbackCampaign | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchCampaigns = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await winbackApi.list(p);
      setCampaigns(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load win-back campaigns.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GenerateWinbackPayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await winbackApi.generate(payload);
      const campaign = res.data.data;
      setActiveCampaign(campaign);
      setCampaigns((prev) => [campaign, ...prev]);
      return campaign;
    } catch (err: any) {
      const msg = err?.message ?? 'Win-back campaign generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteCampaign = useCallback(async (id: number) => {
    try {
      await winbackApi.destroy(id);
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      if (activeCampaign?.id === id) setActiveCampaign(null);
    } catch {
      setError('Failed to delete campaign.');
    }
  }, [activeCampaign]);

  return {
    campaigns, activeCampaign, loading, generating, error, total, page, lastPage,
    fetchCampaigns, generate, deleteCampaign, setActiveCampaign, setError,
  };
}
